/* De kaart "In het kort" op de pagina van een oplossing of dienst (ADR 0024): vier vaste vragen, elk
 * met een kort antwoord en een toelichting die mag ontbreken. In de database is het de JSON-kolom
 * `key_facts`: één object met de sleutels audience, cost, result en duration, elk
 * { "text": antwoord, "note": toelichting }. Alleen een tekst mag ook: "cost": "..." leest de site als
 * { "text": "..." } (lib/kerngegevens.ts in de site-repo).
 *
 * De kolom komt uit een script dat de eigenaar van de database zelf draait. Zolang dat niet is
 * gebeurd, heeft een rij de sleutel `key_facts` niet en wordt hij nooit geschreven (zie ./website).
 *
 * Dezelfde bescherming als bij de blokken (./blokken):
 *   - Het object in het concept is de enige waarheid. Een wijziging verandert alleen het aangeraakte
 *     antwoord.
 *   - Wat er geschreven wordt, is het object uit de database met daarin alleen de aangeraakte
 *     antwoorden vervangen. Sleutels die de bewerker niet kent, blijven dus staan.
 *   - Is geen enkel antwoord anders, dan wordt de kolom niet geschreven.
 *   - Herkent de bewerker de vorm in de database niet, dan blijft de kaart alleen-lezen.
 *   - Een antwoord dat leeg is gemaakt, verdwijnt als sleutel uit het object. */

export const KOLOM = 'key_facts';
/** Het script in de site-repo (greenvision-site) dat de kolom toevoegt. */
export const SCRIPT = 'db/voorstel/2026-10-07-kerngegevens.sql';
export const LABEL = 'In het kort';

export type Kernsleutel = 'audience' | 'cost' | 'result' | 'duration';

/** De vier vragen, in de volgorde van de site (KERNVRAGEN in lib/kerngegevens.ts). */
export const KERNVRAGEN: ReadonlyArray<{ sleutel: Kernsleutel; vraag: string }> = [
  { sleutel: 'audience', vraag: 'Voor wie?' },
  { sleutel: 'cost', vraag: 'Wat kost het?' },
  { sleutel: 'result', vraag: 'Wat levert het op?' },
  { sleutel: 'duration', vraag: 'Hoe lang duurt het?' },
];

export interface Antwoord { tekst: string; toelichting: string }

type Voorwerp = Record<string, unknown>;

const isVoorwerp = (w: unknown): w is Voorwerp => typeof w === 'object' && w !== null && !Array.isArray(w);
const isTekstOfLeeg = (w: unknown): boolean => w == null || typeof w === 'string';
const tekst = (w: unknown): string => (typeof w === 'string' ? w : '');

/** Heeft deze rij de kolom? Een rij uit een database zonder het script heeft de sleutel niet. */
export const heeftKerngegevens = (rij: Record<string, unknown> | null | undefined): boolean => !!rij && KOLOM in rij;

/* ── Eén antwoord ──────────────────────────────────────────────────────────── */

function vormAntwoord(w: unknown): string | null {
  if (w == null || typeof w === 'string') return null;
  if (!isVoorwerp(w)) return 'staat geen antwoord in een vorm die de bewerker kent.';
  if (!isTekstOfLeeg(w.text)) return 'is het antwoord iets anders dan tekst.';
  if (!isTekstOfLeeg(w.note)) return 'is de toelichting iets anders dan tekst.';
  return null;
}

function leesAntwoord(w: unknown): Antwoord {
  if (typeof w === 'string') return { tekst: w, toelichting: '' };
  return isVoorwerp(w) ? { tekst: tekst(w.text), toelichting: tekst(w.note) } : { tekst: '', toelichting: '' };
}

/* De database (jsonb) bewaart de volgorde van sleutels niet: vergelijk dus op inhoud. */
function canoniek(w: unknown): string {
  if (Array.isArray(w)) return `[${w.map(canoniek).join(',')}]`;
  if (isVoorwerp(w)) return `{${Object.keys(w).filter((k) => w[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${canoniek(w[k])}`).join(',')}}`;
  return JSON.stringify(w) ?? 'null';
}

/** Wat een antwoord verder nog bij zich heeft: sleutels die de bewerker niet kent. */
const rest = (w: unknown): string => {
  if (!isVoorwerp(w)) return '{}';
  const { text: _text, note: _note, ...over } = w;
  return canoniek(over);
};

/** Zelfde antwoord? Vergeleken op wat er staat. Geen antwoord, een leeg antwoord en een lege
 *  toelichting tellen als niets; "cost": "..." is hetzelfde als "cost": { "text": "..." }. */
function zelfdeAntwoord(a: unknown, b: unknown): boolean {
  const x = leesAntwoord(a);
  const y = leesAntwoord(b);
  return x.tekst === y.tekst && x.toelichting === y.toelichting && rest(a) === rest(b);
}

/** Een antwoord zoals het geschreven wordt: zonder spaties aan de randen, en zonder lege toelichting.
 *  De vorm blijft: alleen een tekst blijft alleen een tekst, de rest van een object blijft staan. */
function schoonAntwoord(w: unknown): unknown {
  if (typeof w === 'string') return w.trim();
  const { text: _text, note: _note, ...over } = isVoorwerp(w) ? w : {};
  const a = leesAntwoord(w);
  return { ...over, text: a.tekst.trim(), ...(a.toelichting.trim() ? { note: a.toelichting.trim() } : {}) };
}

const isLeegAntwoord = (w: unknown): boolean => {
  const a = leesAntwoord(w);
  return !a.tekst.trim() && !a.toelichting.trim();
};

/* ── De hele kaart ─────────────────────────────────────────────────────────── */

/** `reden` staat er alleen als de vorm onbekend is (ok is dan false). */
export interface Lezing { ok: boolean; antwoorden: Record<Kernsleutel, Antwoord>; reden?: string }

const LEEG = (): Record<Kernsleutel, Antwoord> => ({
  audience: { tekst: '', toelichting: '' }, cost: { tekst: '', toelichting: '' },
  result: { tekst: '', toelichting: '' }, duration: { tekst: '', toelichting: '' },
});

const soortNaam = (w: unknown): string => (typeof w === 'string' ? 'een losse tekst' : Array.isArray(w) ? 'een rij' : typeof w === 'number' ? 'een getal' : 'een losse waarde');

/** Herkent de bewerker deze vorm? Leeg (null) telt als een kaart zonder antwoorden. */
export function leesKerngegevens(waarde: unknown): Lezing {
  if (waarde == null) return { ok: true, antwoorden: LEEG() };
  if (!isVoorwerp(waarde)) return { ok: false, antwoorden: LEEG(), reden: `Er staan geen vier antwoorden maar ${soortNaam(waarde)}.` };
  const antwoorden = LEEG();
  for (const { sleutel, vraag } of KERNVRAGEN) {
    const reden = vormAntwoord(waarde[sleutel]);
    if (reden) return { ok: false, antwoorden: LEEG(), reden: `Bij "${vraag}" ${reden}` };
    antwoorden[sleutel] = leesAntwoord(waarde[sleutel]);
  }
  return { ok: true, antwoorden };
}

/** Eén veld van één antwoord wijzigen. De rest van het object blijft letterlijk staan. Een antwoord
 *  dat alleen een tekst is, blijft dat, tot er een toelichting bij komt: die past alleen in een object. */
export function zetAntwoord(waarde: unknown, sleutel: Kernsleutel, veld: keyof Antwoord, invoer: string): Voorwerp {
  const uit: Voorwerp = isVoorwerp(waarde) ? { ...waarde } : {};
  const oud = uit[sleutel];
  let nieuw: unknown;
  if (typeof oud === 'string') {
    nieuw = veld === 'tekst' ? invoer : invoer === '' ? oud : { text: oud, note: invoer };
  } else {
    const o: Voorwerp = isVoorwerp(oud) ? { ...oud } : {};
    if (veld === 'tekst') o.text = invoer;
    else if (invoer === '') delete o.note;
    else o.note = invoer;
    nieuw = o;
  }
  // Niets meer over: dan staat er ook geen sleutel meer, zoals vóór het typen.
  const kaal = nieuw === '' || (isVoorwerp(nieuw) && Object.keys(nieuw).every((k) => k === 'text') && !tekst(nieuw.text));
  if (kaal) delete uit[sleutel];
  else uit[sleutel] = nieuw;
  return uit;
}

/** Welke antwoorden zijn echt anders dan in de database? Eerst letterlijk vergeleken (niet aangeraakt
 *  is niet gewijzigd), daarna zoals ze geschreven zouden worden (alleen een spatie erbij is niets). */
function gewijzigdeSleutels(opgeslagen: unknown, concept: unknown): Kernsleutel[] {
  const oud: Voorwerp = isVoorwerp(opgeslagen) ? opgeslagen : {};
  const nieuw: Voorwerp = isVoorwerp(concept) ? concept : {};
  return KERNVRAGEN.map((v) => v.sleutel).filter((sleutel) =>
    !zelfdeAntwoord(oud[sleutel], nieuw[sleutel]) && !zelfdeAntwoord(oud[sleutel], isLeegAntwoord(nieuw[sleutel]) ? undefined : schoonAntwoord(nieuw[sleutel])));
}

/** Wat er mis is met de kaart, of null. Alleen een antwoord dat is aangeraakt, wordt nagekeken. */
export function controleerKerngegevens(opgeslagen: unknown, concept: unknown): string | null {
  const basis = leesKerngegevens(opgeslagen);
  const nieuw = leesKerngegevens(concept);
  if (!basis.ok) return canoniek(opgeslagen) === canoniek(concept) ? null : `${LABEL}: deze kaart heeft een vorm die de bewerker niet kent. Hij is hier niet te wijzigen.`;
  if (!nieuw.ok) return `${LABEL}: ${nieuw.reden}`;
  for (const sleutel of gewijzigdeSleutels(opgeslagen, concept)) {
    const { tekst: antwoord, toelichting } = nieuw.antwoorden[sleutel];
    const vraag = KERNVRAGEN.find((v) => v.sleutel === sleutel)!.vraag;
    if (!antwoord.trim() && toelichting.trim()) return `${LABEL}: bij "${vraag}" staat een toelichting zonder antwoord. Vul het antwoord in of haal de toelichting weg.`;
  }
  return null;
}

/** Het object dat geschreven moet worden, of null als er niets te schrijven is: niets gewijzigd, een
 *  onbekende vorm in de database of in het concept, of een antwoord dat niet klopt. De basis is het
 *  object uit de database; alleen de gewijzigde antwoorden zijn vervangen, en een leeg antwoord is weg. */
export function teSchrijven(opgeslagen: unknown, concept: unknown): Voorwerp | null {
  if (!leesKerngegevens(opgeslagen).ok || !leesKerngegevens(concept).ok || controleerKerngegevens(opgeslagen, concept)) return null;
  const gewijzigd = gewijzigdeSleutels(opgeslagen, concept);
  if (gewijzigd.length === 0) return null;
  const uit: Voorwerp = isVoorwerp(opgeslagen) ? { ...opgeslagen } : {};
  const nieuw: Voorwerp = isVoorwerp(concept) ? concept : {};
  for (const sleutel of gewijzigd) {
    if (isLeegAntwoord(nieuw[sleutel])) delete uit[sleutel];
    else uit[sleutel] = schoonAntwoord(nieuw[sleutel]);
  }
  return uit;
}
